import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth';
import { Minus, Plus, AlertTriangle, MessageSquare, ShieldAlert, Clock } from 'lucide-react';
import {
  TID, formatKurus, checkEligibility, getTierForGuests, NOISE_WARNING_TR,
  PLATFORM_MAX_GUESTS, type MansionDetail, type Quote,
} from '@yalidavet/shared';
import { api, ApiError } from '@/lib/api';
import { todayIso, addDaysIso, cn } from '@/lib/utils';

interface Props {
  mansion: MansionDetail;
  initial?: { checkin?: string; checkout?: string; guestCount?: number; eventTypeId?: string };
}

export default function BookingWidget({ mansion, initial }: Props) {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  const [checkin, setCheckin] = useState(initial?.checkin ?? '');
  const [checkout, setCheckout] = useState(initial?.checkout ?? '');
  const [guestCount, setGuestCount] = useState(initial?.guestCount ?? 20);
  const [eventTypeId, setEventTypeId] = useState(
    initial?.eventTypeId ?? mansion.approvedEventTypes[0]?.id ?? '',
  );
  const [addonIds, setAddonIds] = useState<string[]>(
    mansion.addons.filter((a) => a.isRequired).map((a) => a.id),
  );
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [noiseAccepted, setNoiseAccepted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  /* ------------------------------------------------------------------ *
   * Client-side blockers — mirror the server rules so the user is told
   * *immediately*, before any network round trip.
   * ------------------------------------------------------------------ */
  const eligibility = useMemo(
    () =>
      checkEligibility({
        guestCount,
        gardenSizeM2: mansion.gardenSizeM2,
        maxCapacity: mansion.maxCapacity,
        eventTypeId: eventTypeId || undefined,
        approvedEventTypeIds: mansion.approvedEventTypes.map((e) => e.id),
        hasStagingArea: mansion.hasStagingArea,
        hasCateringArea: mansion.hasCateringArea,
      }),
    [guestCount, eventTypeId, mansion],
  );

  const tier = useMemo(() => {
    try { return getTierForGuests(guestCount); } catch { return null; }
  }, [guestCount]);

  const datesValid = !!checkin && !!checkout && checkout > checkin;
  const nightsCount = datesValid
    ? Math.round((Date.parse(`${checkout}T00:00:00Z`) - Date.parse(`${checkin}T00:00:00Z`)) / 86_400_000)
    : 0;
  const minNightsOk = nightsCount >= mansion.minNights;

  /* ------------------------------- Quote ---------------------------- */
  const quoteEnabled = datesValid && minNightsOk && eligibility.eligible && !!eventTypeId;
  const { data: quote, isFetching: quoting } = useQuery<Quote>({
    queryKey: ['quote', mansion.id, checkin, checkout, guestCount, eventTypeId, addonIds],
    queryFn: () => api.quote({ mansionId: mansion.id, checkin, checkout, guestCount, eventTypeId, addonIds }),
    enabled: quoteEnabled,
    retry: false,
  });

  /* ------------------------------ Booking --------------------------- */
  const booking = useMutation({
    mutationFn: () =>
      api.createBooking({
        mansionId: mansion.id, checkin, checkout, guestCount, eventTypeId, addonIds,
        noisePolicyAccepted: true, houseRulesAccepted: true,
      }),
    onSuccess: (res) => navigate(`/rezervasyon/${res.id}/basarili?ref=${res.referenceCode}`),
    onError: (err) => setServerError(err instanceof ApiError ? err.message : 'Rezervasyon oluşturulamadı.'),
  });

  const contactHost = useMutation({
    mutationFn: () => api.startConversation(mansion.id, `Merhaba, ${mansion.title} ilanınız hakkında bilgi almak istiyorum.`),
    onSuccess: (res) => navigate(`/mesajlar/${res.conversationId}`),
  });

  useEffect(() => setServerError(null), [checkin, checkout, guestCount, eventTypeId]);

  /* --------------------- Hard-stop submit gate ---------------------- */
  const blockers: string[] = [];
  if (!eligibility.eligible) blockers.push(eligibility.messageTr);
  if (!datesValid) blockers.push('Giriş ve çıkış tarihlerini seçin.');
  else if (!minNightsOk) blockers.push(`Bu mülk için minimum ${mansion.minNights} gece rezervasyon gereklidir.`);
  if (!eventTypeId) blockers.push('Organizasyon tipini seçin.');
  if (!rulesAccepted) blockers.push('Ev kurallarını onaylayın.');
  if (mansion.isResidentialZone && !noiseAccepted) blockers.push('Gürültü yönetmeliğini onaylayın.');

  const canSubmit = blockers.length === 0 && !!quote?.eligible && !booking.isPending;

  const stepGuests = (delta: number) =>
    setGuestCount((g) => Math.min(Math.max(g + delta, 1), PLATFORM_MAX_GUESTS));

  return (
    <aside className="yd-card sticky top-24 h-fit p-5" data-testid={TID.bookingWidget}>
      {/* Price header */}
      <header className="mb-4 flex items-baseline justify-between" data-testid={TID.bookingPriceHeader}>
        <p>
          <span className="text-2xl font-bold text-ink-900">{formatKurus(mansion.dailyPriceKurus)}</span>
          <span className="text-sm text-ink-500"> / gece</span>
        </p>
        <p className="text-xs text-ink-500">Hafta sonu {formatKurus(mansion.weekendPriceKurus)}</p>
      </header>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-2">
        <label>
          <span className="yd-label">Giriş</span>
          <input
            type="date"
            className="yd-input"
            min={todayIso()}
            value={checkin}
            onChange={(e) => {
              setCheckin(e.target.value);
              if (checkout && checkout <= e.target.value) setCheckout(addDaysIso(e.target.value, 1));
            }}
            data-testid={TID.bookingCheckin}
          />
        </label>
        <label>
          <span className="yd-label">Çıkış</span>
          <input
            type="date"
            className="yd-input"
            min={checkin ? addDaysIso(checkin, 1) : addDaysIso(todayIso(), 1)}
            value={checkout}
            onChange={(e) => setCheckout(e.target.value)}
            data-testid={TID.bookingCheckout}
          />
        </label>
      </div>

      {/* Guest stepper */}
      <div className="mt-4" data-testid={TID.bookingGuestStepper}>
        <span className="yd-label">
          Gece Sayısı / misafir
          <span className="ml-1 font-normal normal-case text-ink-400">
            ({nightsCount || 0} gece · maks. {mansion.maxCapacity} kişi)
          </span>
        </span>
        <div className="flex items-center justify-between rounded-lg border border-ink-200 px-3 py-2">
          <button
            type="button"
            onClick={() => stepGuests(-1)}
            disabled={guestCount <= 1}
            className="grid h-8 w-8 place-items-center rounded-full border border-ink-200 disabled:opacity-40"
            aria-label="Misafir sayısını azalt"
            data-testid={TID.bookingGuestDec}
          >
            <Minus className="h-4 w-4" />
          </button>

          <input
            type="number"
            min={1}
            max={PLATFORM_MAX_GUESTS}
            value={guestCount}
            onChange={(e) => setGuestCount(Math.min(Math.max(Number(e.target.value) || 1, 1), PLATFORM_MAX_GUESTS))}
            className="w-20 border-0 text-center text-sm font-semibold outline-none"
            aria-label="Misafir sayısı"
            data-testid={TID.bookingGuestValue}
            data-guest-count={guestCount}
            data-max-capacity={mansion.maxCapacity}
            data-exceeds-capacity={guestCount > mansion.maxCapacity}
          />

          <button
            type="button"
            onClick={() => stepGuests(1)}
            disabled={guestCount >= PLATFORM_MAX_GUESTS}
            className="grid h-8 w-8 place-items-center rounded-full border border-ink-200 disabled:opacity-40"
            aria-label="Misafir sayısını artır"
            data-testid={TID.bookingGuestInc}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {tier && (
          <p className="mt-1.5 text-xs text-ink-500" data-testid="booking-tier-info" data-tier={tier.tier}>
            Tier {tier.tier} · en az {tier.minGardenM2} m² bahçe gerekir · bu mülk {mansion.gardenSizeM2} m²
          </p>
        )}
      </div>

      {/* Event type */}
      <label className="mt-4 block">
        <span className="yd-label">Organizasyon Tipi</span>
        <select
          className="yd-input"
          value={eventTypeId}
          onChange={(e) => setEventTypeId(e.target.value)}
          data-testid={TID.bookingEventType}
        >
          <option value="">Seçiniz</option>
          {mansion.approvedEventTypes.map((e) => (
            <option key={e.id} value={e.id}>{e.labelTr}</option>
          ))}
        </select>
      </label>

      {/* Add-ons */}
      {mansion.addons.length > 0 && (
        <fieldset className="mt-4">
          <legend className="yd-label">Ödeme Öncesi Hizmetler</legend>
          <div className="space-y-2">
            {mansion.addons.map((a) => {
              const checked = addonIds.includes(a.id);
              const unit =
                a.pricingModel === 'per_guest' ? ' / kişi' : a.pricingModel === 'per_night' ? ' / gece' : '';
              return (
                <label
                  key={a.id}
                  className={cn(
                    'flex cursor-pointer items-start justify-between gap-3 rounded-lg border p-3 text-sm',
                    checked ? 'border-bosphorus-400 bg-bosphorus-50' : 'border-ink-200',
                    a.isRequired && 'cursor-not-allowed opacity-90',
                  )}
                >
                  <span className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-ink-300 text-bosphorus-600"
                      checked={checked}
                      disabled={a.isRequired}
                      onChange={() =>
                        setAddonIds((ids) => (checked ? ids.filter((x) => x !== a.id) : [...ids, a.id]))
                      }
                      data-testid={TID.bookingAddon(a.code)}
                      data-price-kurus={a.priceKurus}
                    />
                    <span>
                      <span className="font-medium text-ink-900">{a.labelTr}</span>
                      {a.isRequired && <span className="ml-1 text-[11px] text-ink-500">(zorunlu)</span>}
                      {a.description && <span className="block text-xs text-ink-500">{a.description}</span>}
                    </span>
                  </span>
                  <span className="shrink-0 font-semibold text-ink-800">
                    {formatKurus(a.priceKurus)}{unit}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      {/* Breakdown */}
      {quote && quote.eligible && (
        <div className="mt-5 space-y-2 border-t border-ink-100 pt-4 text-sm" data-testid="booking-breakdown">
          {quote.lines.map((line, i) => (
            <div key={i} className="flex justify-between gap-3" data-testid={TID.bookingLine} data-line-label={line.label}>
              <span className="text-ink-600">
                {line.label}
                {line.detail && <span className="block text-xs text-ink-400">{line.detail}</span>}
              </span>
              <span
                className="shrink-0 font-medium"
                data-amount-kurus={line.amountKurus}
                {...(line.label.startsWith('Hasar depozitosu') ? { 'data-testid': TID.bookingDeposit } : {})}
              >
                {formatKurus(line.amountKurus)}
              </span>
            </div>
          ))}
          <div className="flex justify-between border-t border-ink-100 pt-3 text-base font-bold">
            <span>Toplam</span>
            <span data-testid={TID.bookingTotal} data-total-kurus={quote.totalKurus}>
              {formatKurus(quote.totalKurus)}
            </span>
          </div>
        </div>
      )}
      {quoting && <div className="yd-skeleton mt-5 h-24" />}

      {/* Neighbourhood constraints */}
      <div className="mt-5 space-y-2">
        <p
          className="flex items-center gap-2 rounded-lg bg-ink-50 px-3 py-2 text-xs text-ink-700"
          data-testid={TID.checkoutDeadline}
          data-deadline={mansion.checkoutDeadline}
        >
          <Clock className="h-4 w-4 shrink-0" aria-hidden />
          Kesin çıkış saati: <strong>{mansion.checkoutDeadline}</strong> · Müzik yayını {mansion.noiseCurfewTime}'da biter
        </p>

        {mansion.isResidentialZone && (
          <label
            className="flex cursor-pointer gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            data-testid={TID.noiseWarning}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-amber-400 text-amber-600"
              checked={noiseAccepted}
              onChange={(e) => setNoiseAccepted(e.target.checked)}
              data-testid="booking-noise-accept"
            />
            <span className="flex gap-1.5">
              <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
              {NOISE_WARNING_TR(mansion.noiseCurfewTime, mansion.checkoutDeadline)}
            </span>
          </label>
        )}

        <label className="flex cursor-pointer items-start gap-2 text-xs text-ink-600">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-bosphorus-600"
            checked={rulesAccepted}
            onChange={(e) => setRulesAccepted(e.target.checked)}
            data-testid="booking-rules-accept"
          />
          Ev kurallarını ve depozito politikasını okudum, kabul ediyorum.
        </label>
      </div>

      {/* Blockers */}
      {(blockers.length > 0 || serverError) && (
        <div
          className="mt-4 space-y-1 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800"
          role="alert"
          data-testid={TID.bookingBlocker}
          data-blocker-reason={eligibility.eligible ? 'FORM_INCOMPLETE' : eligibility.reason}
        >
          <p className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-4 w-4" aria-hidden /> Rezervasyon tamamlanamıyor
          </p>
          <ul className="list-inside list-disc space-y-0.5">
            {serverError && <li>{serverError}</li>}
            {blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>
      )}

      {/* Actions */}
      <button
        type="button"
        className="yd-btn-primary mt-4 w-full"
        disabled={!canSubmit}
        aria-disabled={!canSubmit}
        onClick={() => (isSignedIn ? booking.mutate() : navigate('/giris'))}
        data-testid={TID.bookingSubmit}
        data-can-submit={canSubmit}
      >
        {booking.isPending ? 'Gönderiliyor…' : 'Rezervasyonu Tamamla'}
      </button>

      <button
        type="button"
        className="yd-btn-secondary mt-2 w-full"
        onClick={() => (isSignedIn ? contactHost.mutate() : navigate('/giris'))}
        disabled={contactHost.isPending}
        data-testid={TID.bookingContactHost}
      >
        <MessageSquare className="h-4 w-4" aria-hidden />
        Ev Sahibine Ulaş
      </button>

      <p className="mt-3 text-center text-[11px] text-ink-400">
        Henüz ücretlendirilmeyeceksiniz. Ev sahibi onayından sonra ödeme alınır.
      </p>
    </aside>
  );
}
